import React, { Component } from 'react';
import { connect } from 'react-redux';
import lodashGet from 'lodash/get';
import { Popconfirm, message, Icon } from 'antd';
import { timer } from 'rxjs';
import PreFlightOptimisationModal from './PreFlightOptimisationModal';
import baseActions from '../actions';
import {
  CONFIG, PERSIST_PARAMS, OPTIMISE, OPTIMISE_STATUS, OPTIMISE_ISSUES, SCENARIOS,
  PUBLISH_SHIFT, PUBLISH_STATUS, CHECK_IS_OPTIMISE, COPY_SHIFTS, CLEAR_REPLAN,
  CANCEL_STATUS, CANCEL_OPTIMISE,
} from '../plannerConfig';
import { updateLink, getSetSequence, handleClickToViewScenario } from '../helpers';
import SubNavigation from '../components/WithSubNavigation';
import { SubNavButton } from '../components/WithSubNavigation/styles';
import {
  REPLAN_ACCEPT_CONFIRMATION, REPLAN_REJECT_CONFIRMATION, PUBLISH_SUCCESS,
  COPY_SHIFTS_SUCCESS,
} from '../constants/text.constants';
import ScenarioComparisonWrapper from '../components/ScenarioComparison/ComparisonWrapper';
import { SCENARIO_REPLAN_ACTIVE_ENUM } from '../constants/app.constants';
import { InlineSpinner } from '../styles';


const mapStateToProps = state => ({
  [CONFIG]: state[CONFIG].data,
  [PERSIST_PARAMS]: state[PERSIST_PARAMS].data,
  [OPTIMISE]: state[OPTIMISE].data, // This is for polling messages.
  [CHECK_IS_OPTIMISE]: state[CHECK_IS_OPTIMISE].data, // This is used only for Active Plan
  [OPTIMISE_STATUS]: state[OPTIMISE_STATUS].data,
  [OPTIMISE_ISSUES]: state[OPTIMISE_ISSUES].data,
  [PUBLISH_STATUS]: state[PUBLISH_STATUS].data.status,
  [CANCEL_STATUS]: state[CANCEL_STATUS].data.status,
  [SCENARIOS]: state[SCENARIOS].data.results,
  [COPY_SHIFTS]: state[COPY_SHIFTS],
});

const mapDispatchToProps = {
  get: baseActions.get,
  post: baseActions.post,
  set: baseActions.set,
  clear: baseActions.clear,
};

const DecisionButton = ({
  confirmMessage,
  text,
  onConfirm,
  noConfirm,
  ...rest
}) => {
  const Wrapper = props => noConfirm
    ? <span {...props} />
    : <Popconfirm {...props} />;

  return (
    <Wrapper
      {...!noConfirm && { onConfirm }}
      title={confirmMessage}
      placement="bottomRight"
    >
      <SubNavButton
        color="greyscale.5"
        type="button"
        {...rest}
      >
        {text}
      </SubNavButton>
    </Wrapper>
  );
};

let poll;

const withSubNavigation = (ChildComponent) => {
  class SubNavigationWithComponent extends Component {
    state = {
      mounted: false,
      waitingOptimiseStatus: true,
      drawerVisible: false,
      compare: [],
      preflightModalOpen: false,
      preflightOptions: {},
      replanPending: false,
    };

    componentDidMount() {
      const { history, set } = this.props;
      set([OPTIMISE_STATUS], { status: 'CHECKING' });
      getSetSequence(this.props, [
        [SCENARIOS],
      ]);

      this.setState({ mounted: true }, () => {
        this.checkIsOptimising();
        history.listen(() => {
          // This prevent double checkIsOptimising when the component is mounted.
          const { mounted } = this.state;
          if (!mounted) return false;

          this.interuptPoll();
          this.setState({ waitingOptimiseStatus: true }, () => {
            setTimeout(this.checkIsOptimising, 400);
          });
          return true;
        });
      });
    }

    // This is usually called when user event is triggered and requires poll
    // to be stopped - e.g. switch scenario.
    // This is called multiple times to ensure killing the previous poll.
    interuptPoll = () => {
      const { set } = this.props;
      set([OPTIMISE_STATUS], { status: 'CHECKING' });
      this.setState({ waitingOptimiseStatus: true });
    }

    // Promise + timeout out is used for a short delay to give double assurance nothing is under
    // processing before giving the controls back to users.
    allowUserEvent = () => setTimeout(() => this.setState({ waitingOptimiseStatus: false }), 400);

    checkIsOptimising = (onlyCheckWeekAreOpt) => {
      const {
        persistParams, get, set, clear,
      } = this.props;
      const { scenario, startOfWeekDate, endOfWeekDate } = persistParams;
      // A debounce is added in here while stopping the poll. This is because when the poll
      // is stopped too quickly, the UI does not have sufficient time to react to change
      // the status display.
      const debounceStopPoll = () => {
        setTimeout(() => {
          if (poll) poll = poll.unsubscribe();
        }, 1000);
      };

      if (!onlyCheckWeekAreOpt) this.interuptPoll();
      get( // This checks whether the scenario is optimise or not.
        [CHECK_IS_OPTIMISE],
        {
          routeParams: {
            scenario,
            startDate: startOfWeekDate,
            endDate: endOfWeekDate, // Optimise has to be done for entire week.
          },
          callback: (data) => {
            if (!onlyCheckWeekAreOpt) {
              clear([OPTIMISE, OPTIMISE_STATUS]);
              // The "poll" variable is defined above and set in here so that the system knows what
              // polling is to subscribe and unsubscribe.
              set([OPTIMISE_STATUS], { awaitIssue: true });

              if (poll) poll = poll.unsubscribe();
              poll = this.optimisePolling(data.id);
              this.allowUserEvent();
            }

            // This is only used for active plan to check whether any other week is optimising.
            // There was a decision made to check if any other week in active plan is
            // optimising, disable editing. Ideally, such ban one day can be lifted.
            // TODO: The current checking pattern is conducting twice, the backend for
            // CHECK_IS_OPTIMISING passes one extra prop - otherWeekAreOptimising. Would need
            // to do this a bit better.
            if (parseInt(scenario, 0) === 0) {
              set([OPTIMISE_STATUS], {
                activePlanOtherWeekIsOptimising: data.otherWeekAreOptimising,
              });

              if (!data || !data.otherWeekAreOptimising) {
                debounceStopPoll();
                set([OPTIMISE_STATUS], { status: data.status });
                this.allowUserEvent();
              }
            } else if (onlyCheckWeekAreOpt) {
              debounceStopPoll(); // Scenario no need to check other weeks status.
            }
          },
        },
      );
    }

    handleClickToPublish = () => {
      const { get, set, persistParams: { startOfWeekDate, endOfWeekDate } } = this.props;
      set([PUBLISH_STATUS], { status: true });

      get(
        [PUBLISH_SHIFT],
        {
          routeParams: {
            startDate: startOfWeekDate,
            endDate: endOfWeekDate,
          },
          callback: () => {
            this.publishSuccess();
          },
          errorCallback: () => {
            this.publishFail();
          },
        },
      );
    };

    handleClickToCancel = () => {
      const { post, set, persistParams: { scenario, startOfWeekDate, endOfWeekDate } } = this.props;
      set([CANCEL_STATUS], { status: true });

      post(
        [CANCEL_OPTIMISE],
        {
          routeParams: {
            scenario,
            startDate: startOfWeekDate,
            endDate: endOfWeekDate,
          },
          callback: () => {
            set([CANCEL_STATUS], { status: false });
          },
          errorCallback: () => {
            set([CANCEL_STATUS], { status: false });
          },
        },
      );
    };

    publishSuccess = () => {
      const { set } = this.props;
      set([PUBLISH_STATUS], { status: false });
      message.success(PUBLISH_SUCCESS);
    }

    publishFail = () => {
      const { set } = this.props;
      set([PUBLISH_STATUS], { status: false });
    }

    startOptimisation = (overrideStartTime) => {
      const { post, set, persistParams } = this.props;
      const { preflightOptions: { optimisationOptions: { usePrevPlan } } } = this.state;
      const { scenario, startOfWeekDate, endOfWeekDate } = persistParams;
      this.handleOptimisationPreflightCancel();
      this.interuptPoll();
      set([OPTIMISE_STATUS], { status: 'PREPARING' });

      post(
        [OPTIMISE],
        {
          routeParams: {
            scenario,
            startDate: startOfWeekDate,
            endDate: endOfWeekDate, // Optimise has to be done for entire week.
          },
          body: {
            // If a start time provided, it comes in the form of a moment object
            start: overrideStartTime || null,
            consider_previous_plan: usePrevPlan,
          },
          callback: (data) => {
            if (data.id) {
              poll = this.optimisePolling(data.id);
            }
          },
          errorCallback: () => {
            set([OPTIMISE_STATUS], { status: 'FAILED' });
          },
        },
      );
    };

    handleOptimisationPreflightCancel = () => {
      this.setState({
        preflightModalOpen: false,
        preflightOptions: {},
      });
    };

    handleClickToOptimise = (optimisationOptions) => {
      const { persistParams } = this.props;
      this.setState({
        preflightModalOpen: true,
        preflightOptions: { optimisationOptions, ...persistParams },
      });
    };

    handleClickToUpdateLink = (obj) => {
      this.interuptPoll();
      updateLink(obj, this.props);
    }

    optimisePolling = (jobId) => { // Backend uses id instead of jobId.
      const { get, set } = this.props;
      // This is now a constant poll...NOT! Constant only when editing is turned off.
      // Apparently constant poll would break Bkt-React Grid.
      // Change anything above 5 seconds is way too slow for it.
      // As long as websocket is not place, please respect this time constraint for usability.
      // Any server performance concern should take the effort to optimise the endpoint calls,
      // rather than modifying the time of this timer.
      return timer(0, 5000)
        .subscribe(() => {
          // If there is no jobId, just check other week.
          if (!jobId) {
            // No jobId, don't know whether this is scenario or active plan. The below function
            // can help deciding.
            return this.checkIsOptimising(true);
          }

          get(
            [OPTIMISE],
            {
              routeParams: { jobId },
              callback: (data) => {
                // If status is SUCCEEDED, FAILED or CANCELLED, remove the poll and reset the status
                // of the view status button.
                if (
                  data.status === 'SUCCEEDED'
                  || data.status === 'FAILED'
                  || data.status === 'CANCELLED'
                ) {
                  const { optimiseStatus: { awaitIssue } } = this.props;
                  // Here used to be a constant poll, but apparently constant poll breaks the
                  // grid saving, so...the code below would still works fine but can be refactored
                  // better.
                  if (awaitIssue) {
                    set([OPTIMISE_STATUS], { awaitIssue: false });
                  }

                  // A scenario only has a week, so no need to reconsider other weeks.
                  if (data && data.scenarioId !== 0) {
                    if (poll) poll = poll.unsubscribe();
                  }
                }

                if (data.status === 'RUNNING') {
                  // Issues list is to be retrieve after running. Set the status to true
                  // so that issue can be retrieved later.
                  set([OPTIMISE_STATUS], { awaitIssue: true });
                } else if (data.scenarioId === 0) {
                  // Final check and see if is active plan and other week is optimising.
                  // This would retain the optimising status is other week is optimising
                  // in active plan.
                  this.checkIsOptimising(true);
                }

                set([OPTIMISE_STATUS], { status: data.status });
                this.allowUserEvent();
              },
            },
          );
        });
    }

    handleClickToAcceptReplan = () => {
      const { post } = this.props;
      const { compare } = this.state;

      this.setState({ replanPending: true });

      post(
        [COPY_SHIFTS],
        {
          routeParams: {
            scenario: compare[1].id,
            scenarioDest: compare[0].id,
          },
          // data.messages is the url of messages.
          callback: () => {
            message.success(COPY_SHIFTS_SUCCESS(compare[1].name));
            this.setState({ replanPending: false });
            // once accepted, close off replan so it doesn't show up again
            this.decideReplan();
          },
        },
      );
    }

    handleClickToRejectReplan = () => {
      const { post } = this.props;
      const { compare } = this.state;
      post(
        [CLEAR_REPLAN],
        {
          routeParams: {
            scenario: compare[1].id,
          },
          callback: () => {
            this.decideReplan();
          },
        },
      );
      this.decideReplan();
    }

    decideReplan = () => {
      this.setState({ drawerVisible: false });
      this.checkIsOptimising();
    }

    handleDrawerCancel = () => {
      this.setState({
        drawerVisible: false,
      });
    }

    onScenarioComparisonClick = (data, isDashboard) => {
      handleClickToViewScenario(data, isDashboard, this.props);
      this.handleDrawerCancel();
    };


    handleClickToShowDrawer = () => {
      const {
        scenarios = [], optimise: {
          replanScenarioId, replanScenarioName, fromDate, toDate,
        },
      } = this.props;
      // If no replan scenario id given, just don't do anything as oppose as
      // breaking the app.
      if (!replanScenarioId) {
        return false;
      }

      // We recreate the replan scenario from the data returned from the optimisation poll.
      // We do this is to avoid a race condition with scenarios in state being out of sync.
      const replanScenario = {
        id: replanScenarioId,
        name: replanScenarioName,
        fromDate,
        toDate,
      };

      return this.setState({
        drawerVisible: true,
        compare: [scenarios[0], replanScenario],
      });
    }

    render() {
      const {
        config, get, post, set, clear, persistParams, optimise, optimiseStatus, optimiseIssues,
        publishStatus, cancelStatus, copyShifts, checkIsOptimise, ...rest
      } = this.props;

      const {
        drawerVisible, waitingOptimiseStatus, experimental,
        preflightModalOpen, preflightOptions, compare, replanPending,
      } = this.state;

      const replanDisabled = lodashGet(checkIsOptimise, 'data.replanScenarioState', null) === SCENARIO_REPLAN_ACTIVE_ENUM
        || replanPending;

      return (
        <>
          <SubNavigation
            api={{
              get, post, set, clear,
            }}
            config={config}
            persistParams={persistParams}
            optimise={optimise}
            activePlanOtherWeekIsOptimising={optimiseStatus.activePlanOtherWeekIsOptimising}
            optimiseStatus={optimiseStatus.status}
            optimiseIssues={optimiseIssues}
            waitingOptimiseStatus={waitingOptimiseStatus}
            onClickToUpdateLink={this.handleClickToUpdateLink}
            onClickToOptimise={this.handleClickToOptimise}
            onClickToShowOptResult={this.handleClickToShowDrawer}
            onClickToPublish={this.handleClickToPublish}
            onClickToCancel={this.handleClickToCancel}
            publishStatus={publishStatus}
            cancelStatus={cancelStatus}
            isCopyShifts={copyShifts.loadingQueue.length > 0}
            experimental={experimental}
            {...rest}
          />
          <ChildComponent {...this.props} />
          <ScenarioComparisonWrapper
            title="Compare Replan Result"
            selectedScenarios={compare}
            onScenarioClick={this.onScenarioComparisonClick}
            handleDrawerCancel={this.handleDrawerCancel}
            visible={drawerVisible}
            titleBarActions={(
              <div>
                {replanDisabled ? (
                  <InlineSpinner>
                    <Icon type="sync" spin />
                    <span>Accepting Replan</span>
                  </InlineSpinner>
                ) : (
                  <>
                    <DecisionButton
                      confirmMessage={REPLAN_ACCEPT_CONFIRMATION}
                      text="Accept"
                      bg="proceed"
                      mr={1}
                      onConfirm={this.handleClickToAcceptReplan}
                    />
                    <DecisionButton
                      confirmMessage={REPLAN_REJECT_CONFIRMATION}
                      text="Reject"
                      bg="reject"
                      noConfirm
                      mr={3}
                      onClick={this.handleClickToRejectReplan}
                    />
                  </>
                )}
              </div>
            )}
          />
          {preflightModalOpen && (
            <PreFlightOptimisationModal
              preflightCheckProps={preflightOptions}
              onCancel={this.handleOptimisationPreflightCancel}
              onSubmit={this.startOptimisation}
            />
          )}
        </>
      );
    }
  }

  return connect(mapStateToProps, mapDispatchToProps)(SubNavigationWithComponent);
};

export default withSubNavigation;
